import {
    AddressInput,
    CreateMapRequestType,
    CreateMapRequestView,
    CreateOrUpdateMapRequestInput, ICreateOrUpdateMapRequestInput,
    IUserData
} from '@/api/klip-api.proxy';
import {computed, defineComponent, onMounted, ref, watch} from 'vue';
import {first} from 'lodash-es';
import {useFormatting} from '@/composables/formatting';
import OrganisationHelper from '@/app/shared/helpers/organisation-helper';
import {escape} from 'html-escaper';
import {Mri} from '@/app/mri/shared/services/services';
import * as yup from 'yup';
import {createTagsSchema} from '@/app/shared/components/kl-tags/kl-tags';
import {createAddressSchema} from '@/app/shared/components/kl-address-form/kl-address-form';
import {useForm} from 'vee-validate';
import {toTypedSchema} from '@vee-validate/yup';
import {
    klValidationFields,
    ValidationFieldsType
} from '@/app/shared/components/kl-form-fields/kl-field-validation-util';
import userLocalStorage from '@/storage/user-local-storage';
import DateUtil from '@/app/shared/helpers/date-util';
import {useMounted} from '@vueuse/core';
import KlFormRow from '@/app/shared/components/kl-form-row/kl-form-row.vue';
import {DirectiveBinding} from 'vue';

export interface IKlMriCreateStep2Result {
    maprequestInput: CreateOrUpdateMapRequestInput;
    isValid: boolean;
}

interface IContactDetails {
    name: string;
    role: number;
    telephone: string;
    email: string;
    address: Mri.IAddress;
    organisationId: string;
    addressAdditionalLine: string;
}

type ColumnHalfBinding = {
    width?: string | number;
    widthXs?: string | number;
};

const setColumnWidthAttributes = (el: HTMLElement, binding: DirectiveBinding<ColumnHalfBinding | undefined>) => {
    const width = binding.value?.width ?? '4';
    const widthXs = binding.value?.widthXs ?? '12';

    el.setAttribute('width', String(width));
    el.setAttribute('width-xs', String(widthXs));
};

export default defineComponent({
    name: 'KlMriCreateStep2',
    components: {KlFormRow},
    emits: ['submit'],
    props: {
        isCreate: {type: Boolean, required: false, default: true},
        user: {type: Object as () => IUserData, required: false, default: null},
        viewModel: {type: Object as () => CreateMapRequestView, required: false, default: null},
        maprequestType: {type: String, required: false, default: null},

        prefill: {type: Object as () => ICreateOrUpdateMapRequestInput, required: false, default: null},
    },
    setup(props, { emit }) {

        const isMounted = useMounted();

        const excavationTypeLocalStorageKey = 'mri_create_pa_excavationtype';
        const excavationMethodLocalStorageKey = 'mri_create_pa_excavationmethod';
        const contactLocalStorageKey = 'mri_create_pa_contact_details';

        const referentieValidationRegex = /^(?!.*([²³]))/;
        const schema = yup.object({
            excavationType: yup.string().required().label('Aard van de werken'),
            excavationMethod: yup.string().required().label('Geplande uitvoeringsmethode'),
            isKlipDecree: yup.boolean().notRequired(),
            startDate: yup.array().kl_required_date().kl_date_later_then({minDate: DateUtil.today()}).label('Startdatum')
                .when('isKlipDecree', {is: true, then: schema => schema.kl_max_40_days_ahead()}),
            endDate: yup.array().kl_required_date().kl_date_later_then({minDateField: 'startDate'}).label('Einddatum'),
            excavationDepth: yup.string().kl_number({min: 0, max: 10000}).notRequired().label('Graafdiepte'),
            includeImkl: yup.boolean().notRequired(),
            reference: yup.string().required().max(100).matches(referentieValidationRegex).label('Uw referentie'),
            excavationLocation: yup.string().notRequired().max(200).label('Locatie werken'),
            excavationDescriptionRequired: yup.boolean().notRequired(),
            excavationDescription: yup.string().notRequired().max(400).label('Omschrijving werken')
                .when('excavationDescriptionRequired', {is: true, then: schema => schema.required()}),
            tags: createTagsSchema(),
            isOrganisation: yup.boolean().notRequired(),
            organisationId: yup.string().notRequired().label('Aanvragende organisatie')
                .when('isOrganisation', {is: true, then: schema => schema.required()}),
            role: yup.string().required().label('Uw rol'),
            address: createAddressSchema(),
            addressAdditionalLine: yup.string().notRequired().max(200).label('Gebouw'),
            telephone: yup.string().required().kl_phone().max(20).label('Telefoon'),
            emailAddress: yup.string().required().email().max(320).label('E-mailadres'),
            useAlternativeEmail: yup.boolean().notRequired(),
            alternateEmailAddress: yup.string().notRequired().email().label('Alternatief e-mailadres')
                .when('useAlternativeEmail', {is: true, then: schema => schema.required()})
        });

        const _form = useForm({
            validationSchema: toTypedSchema(schema),
            initialValues: {
                address: {
                    countryCode: 'BE',
                },
                useAlternativeEmail: false,
                tags: [],
            }
        });
        const fields: ValidationFieldsType<typeof schema> = klValidationFields<typeof schema>(schema);

        const excavationType = fields.excavationType.getValue<string>();
        const excavationMethod = fields.excavationMethod.getValue<string>();
        const isKlipDecree = fields.isKlipDecree.getValue<boolean>();
        const startDate = fields.startDate.getValue<string[]>();
        const endDate = fields.endDate.getValue<string[]>();
        const excavationDepth = fields.excavationDepth.getValue<number>();
        const includeImkl = fields.includeImkl.getValue<boolean>();
        const reference = fields.reference.getValue<string>();
        const excavationLocation = fields.excavationLocation.getValue<string>();
        const excavationDescriptionRequired = fields.excavationDescriptionRequired.getValue<boolean>();
        const excavationDescription = fields.excavationDescription.getValue<string>();
        const tags = fields.tags.getValue<string[]>();
        const isOrganisation = fields.isOrganisation.getValue<boolean>();
        const organisationId = fields.organisationId.getValue<string>();
        const role = fields.role.getValue<string>();
        const addressAdditionalLine = fields.addressAdditionalLine.getValue<string>();
        const address = fields.address.getValue<Mri.IAddress>();
        const telephone = fields.telephone.getValue<string>();
        const emailAddress = fields.emailAddress.getValue<string>();
        const useAlternativeEmail = fields.useAlternativeEmail.getValue<boolean>();
        const alternateEmailAddress = fields.alternateEmailAddress.getValue<string>();

        const _reasonIsExcavationType = computed((): boolean => ['othersubterranean', 'otheroverground'].includes(excavationType.value));
        const _reasonIsExcavationMethod = computed((): boolean => excavationMethod.value === 'specialexcavationmethod');

        const hasSubOrganisations = computed((): boolean => {
            if (props.user.isCitizen) {
                return false;
            }
            return !!props.viewModel?.subOrganisations && props.viewModel.subOrganisations.length > 1;
        });

        const contactDetails = ref<IContactDetails>(null);
        const editContactDetailsMode = ref<boolean>(false);
        const rememberContactDetails = ref<boolean>(false);

        // const rememberOrganisation = ref<boolean>(false);
        // const organisationPrefilledByCookie = ref<boolean>(false);

        const showStep2ValidationInfo = ref<boolean>(false);

        const startDateMinDate = ref<string>(DateUtil.formatDate(DateUtil.today()));
        const endDateMinDate = computed(() => { return first(startDate.value); });

        const showOrgDropDown = computed((): boolean => {
            return hasSubOrganisations.value && editContactDetailsMode.value;
        });
        const showContactCard = computed((): boolean => {
            return contactDetails.value && !editContactDetailsMode.value;
        });

        const {formatCurrency} = useFormatting();

        const setDepth = (depth: number, name: string) => {
            return OrganisationHelper.setDepth(depth, name)
        }

        const userHasNoEmail = computed((): boolean => {
            return !props.user.email;
        });

        const canOrderImkl = computed((): boolean => {
            return !props.user.isCitizen;
        });

        const applyVat = computed((): boolean => {
            return props.user.applyVat;
        });

        const contactAddress = computed((): string => {
            if (!contactDetails.value) {
                return null;
            }

            const address = contactDetails.value.address;
            const additionalLine = address.additionalLine ? `${escape(address.additionalLine)}<br>` : '';
            const street = `${address.street ? escape(address.street) : ''}${address.street && address.streetNumber ? ` ${escape(address.streetNumber)}` : ''}${address.box ? ` ${escape(address.box)}` : ''}<br>`;
            const cityCountry = `${address.zip && address.city ? `${escape(address.zip)} ` : ''}${address.city ? escape(address.city) : ''}${address.city && address.countryCode ? ` - ` : ''}${address.countryCode ? escape(address.countryCode) : ''}`;

            return `${additionalLine}${street}${cityCountry}`;
        });

        const _findSelectedOrganisationName = (orgId: string): string => {
            const selectedSubOrganisation = props.viewModel.subOrganisations.find(x => x.key === orgId);
            return selectedSubOrganisation ? selectedSubOrganisation.label : null;
        }

        const contactRole = computed(() => {
            const orgName = _findSelectedOrganisationName(organisationId.value) ;

            const hasRole = contactDetails.value?.role;
            const roleString = hasRole ? props.viewModel.mapRequestRoles[contactDetails.value.role] : '';
            const userString = orgName ? `${contactDetails.value.role ? ' - ' : ''}${escape(orgName)}` : '';
            return `${roleString}${userString}`;
        });

        /**
         * mapRequestRoles are received from the API as
         * an object in alphabetical order
         * This computed property transforms mapRequestRoles
         * to an array with options starting with 'other'
         * at the end
         * @NOTE - Should probably be sorted in backend.
         */
        const mapRequestRoles = computed(() => {
            if (!props.viewModel.mapRequestRoles) {
                return null;
            }
            const roles = props.viewModel.mapRequestRoles;
            const rolesArray = Object.keys(roles).map((key: string) => {
                return {key, value: roles[key]};
            });
            return rolesArray.filter((item) => !item.key.includes('other'))
                .concat(rolesArray.filter((item) => item.key.includes('other')));
        });

        const editContactDetails = () => {
            editContactDetailsMode.value = true;
        }

        const _removeContactDetails = () => {
            userLocalStorage.removeItem(contactLocalStorageKey);
        }

        const _saveContactDetails = () => {
            if (address.value) {
                userLocalStorage.setItem(contactLocalStorageKey, {
                    name: `${props.user.firstName} ${props.user.lastName}`,
                    role: role.value,
                    telephone: telephone.value,
                    email: userHasNoEmail.value ?
                        emailAddress.value :
                        alternateEmailAddress.value && useAlternativeEmail.value ? alternateEmailAddress.value : emailAddress.value,
                    address: {...address.value, additionalLine: addressAdditionalLine.value},
                    addressAdditionalLine: addressAdditionalLine.value,
                    organisationId: organisationId.value,
                }, true);
            }
        }

        const _parseMapRequestInput = async (createMapRequestInput: ICreateOrUpdateMapRequestInput) => {
            if (!createMapRequestInput) {
                //_form.resetForm({values: _form.values});
                return;
            }

            excavationType.value = createMapRequestInput.excavationType;
            excavationMethod.value = createMapRequestInput.excavationMethod;
            excavationDepth.value = createMapRequestInput.excavationDepth;
            reference.value = createMapRequestInput.reference;
            excavationLocation.value = createMapRequestInput.excavationLocation;
            excavationDescription.value = createMapRequestInput.excavationDescription;
            tags.value = createMapRequestInput.tags;

            if (createMapRequestInput.startDate) {
                startDate.value = [DateUtil.formatDate(createMapRequestInput.startDate)];
            }
            if (createMapRequestInput.endDate) {
                endDate.value = [DateUtil.formatDate(createMapRequestInput.endDate)];
            }

            if (createMapRequestInput.address) {
                address.value = {
                    additionalLine: createMapRequestInput.address.additionalLine,
                    street: createMapRequestInput.address.street,
                    streetNumber: createMapRequestInput.address.streetNumber,
                    box: createMapRequestInput.address.box,
                    city: createMapRequestInput.address.city,
                    zip: createMapRequestInput.address.zip,
                    countryCode: createMapRequestInput.address.countryCode,
                };
            }
            if (createMapRequestInput.role) {
                role.value = createMapRequestInput.role;
            }
            if (createMapRequestInput.organisationId) {
                organisationId.value = createMapRequestInput.organisationId;
            }
            if (createMapRequestInput.telephone) {
                telephone.value = createMapRequestInput.telephone;
            }
            includeImkl.value = createMapRequestInput.includeImkl;
        }

        const _validateAndFixStartEndDates = () => {
            // fix problems with DateTimeControls:
            // - startDate control kan zeer raar gedrag vertonen indien de start & end in het verleden liggen
            const today = DateUtil.today();
            if (startDate.value && (DateUtil.parseToDayJs(startDate.value) < today)) {
                startDate.value = null;
            }
            if (endDate.value) {
                if (startDate.value && (DateUtil.parseToDayJs(endDate.value) < DateUtil.parseToDayJs(startDate.value))) {
                    endDate.value = null;
                }
                else if (DateUtil.parseToDayJs(endDate.value) < today) {
                    endDate.value = null;
                }
            }
        }

        // sync to form field
        const _isKlipDecree = computed((): boolean => props.maprequestType === 'klipdecree');
        watch(_isKlipDecree, (newValue: boolean) => {
            isKlipDecree.value = newValue;
        }, {immediate: true});
        // sync to form field
        const _excavationDescriptionRequired = computed((): boolean => _reasonIsExcavationType.value || _reasonIsExcavationMethod.value);
        watch(_excavationDescriptionRequired, (newValue: boolean) => {
            excavationDescriptionRequired.value = newValue;
        }, {immediate: true});
        // sync to form field
        watch(showOrgDropDown, (newValue: boolean) => {
            isOrganisation.value = newValue;
        }, {immediate: true});


        watch(
            excavationType,
            () => {
                _saveExcavationType();
            },
            {immediate: false, deep: false})

        watch(
            excavationMethod,
            () => {
                _saveExcavationMethod();
            },
            {immediate: false, deep: false})


        const _saveExcavationType = () => {
            if (excavationType.value !== 'othersubterranean' && excavationType.value !== 'otheroverground') {
                userLocalStorage.setItem(excavationTypeLocalStorageKey, excavationType.value, true);
            }
        }

        const _saveExcavationMethod = () => {
            userLocalStorage.setItem(excavationMethodLocalStorageKey, excavationMethod.value, true);
        }

        const isStep2Valid = computed(() => {
            return _form.meta.value.valid;
        });

        /**
         * The message beneath period date selection should
         * only be shown when type = grondwerken and start date
         * is less than 5 days from now
         */
        const showPeriodMessage = computed(() => {
            if (!startDate.value || !startDate.value[0]) {
                return '';
            }
            const diff = DateUtil.parseToDayJs(startDate.value).diff(DateUtil.today(), 'day');
            return _isKlipDecree.value && diff < 5;
        });

        const excavationDescriptionWarning = computed((): string => {
            let warning = 'Verplicht bij selectie van';
            if (_reasonIsExcavationType.value) {
                warning += ' andere aard van de werken';
            }
            if (_reasonIsExcavationType.value && _reasonIsExcavationMethod.value) {
                warning += ' en';
            }
            if (_reasonIsExcavationMethod.value) {
                warning += ' bijzondere uitvoeringsmethoden';
            }
            warning += '.';

            return warning;
        });

        onMounted(async () => {
           _prefillPreferences();

           await _parseMapRequestInput(props.prefill);
            _validateAndFixStartEndDates();

           // TODO: review/remove/move???
            // set user defaults
            if (props.user?.email) {
                emailAddress.value = props.user.email;
            }
            if (!hasSubOrganisations.value) {
                organisationId.value = props.user.klipOrganisationApiId;
            }
        });

        const _prefillPreferences = () => {
            _fillPreferredContactDetails();
            _fillPreferredExcavationType();
            _fillPreferredExcavationMethod();
        }

        const _fillPreferredContactDetails = () => {
            contactDetails.value = userLocalStorage.getItem(contactLocalStorageKey, true);

            if (contactDetails.value) {
                rememberContactDetails.value = true;

                role.value = contactDetails.value.role.toString();
                telephone.value = contactDetails.value.telephone;
                alternateEmailAddress.value = contactDetails.value.email;

                if (userHasNoEmail.value) {
                    emailAddress.value = contactDetails.value.email;
                }

                address.value = contactDetails.value.address;
                addressAdditionalLine.value = contactDetails.value.addressAdditionalLine;
                organisationId.value = contactDetails.value.organisationId;
            }

            useAlternativeEmail.value = alternateEmailAddress.value && alternateEmailAddress.value !== props.user.email;
            editContactDetailsMode.value = !contactDetails.value;

            // check if this is a valid organisationId
            if (!!organisationId.value) {
                const selectedSubOrganisation = _findSelectedOrganisationName(organisationId.value);
                if (!selectedSubOrganisation){
                    organisationId.value = null;
                }
            }
            // If the organisationId is not found in the subOrganisations, we still want to edit the contact details
            if (!organisationId.value) {
                editContactDetails();
            }
        }

        const _fillPreferredExcavationType = () => {
            const preferred = userLocalStorage.getItem(excavationTypeLocalStorageKey, true);
            if (props.isCreate && preferred) {
                excavationType.value = preferred;
            }
        }

        const _fillPreferredExcavationMethod = () => {
            const preferred = userLocalStorage.getItem(excavationMethodLocalStorageKey, true);
            if (props.isCreate && preferred) {
                excavationMethod.value = preferred;
            }
        }

        const _createMapRequestsInput = (): CreateOrUpdateMapRequestInput => {
            return new CreateOrUpdateMapRequestInput({
                organisationId: organisationId.value,
                reference: reference.value,
                telephone: telephone.value,
                email: alternateEmailAddress.value && useAlternativeEmail.value ? alternateEmailAddress.value : emailAddress.value,
                role: role.value,
                address: new AddressInput({
                    additionalLine: addressAdditionalLine.value,
                    street: address.value.street,
                    streetNumber: address.value.streetNumber,
                    box: address.value.box,
                    city: address.value.city,
                    zip: address.value.zip,
                    countryCode: address.value.countryCode,
                }),
                excavationType: excavationType.value,
                excavationDescription: excavationDescription.value,
                excavationLocation: excavationLocation.value,
                excavationMethod: excavationMethod.value,
                excavationDepth: excavationDepth.value,
                startDate: DateUtil.formatForApi(first(startDate.value), 'YYYY/MM/DD'),
                endDate: DateUtil.formatForApi(first(endDate.value), 'YYYY/MM/DD'),
                includeImkl: includeImkl.value,
                tags: tags.value,
            });
        }

        const _submitForm = async () => {
            if (rememberContactDetails.value) {
                _saveContactDetails();
            } else {
                _removeContactDetails();
            }
            showStep2ValidationInfo.value = false;


            emit('submit', <IKlMriCreateStep2Result>{
                maprequestInput:  _createMapRequestsInput(),
                isValid: isStep2Valid.value, // kan nooit false zijn op dit punt..?
            });
        }

        const _onInvalidSubmit = (submittedValues) => {
            console.log('_onInvalidSubmit', submittedValues);

            showStep2ValidationInfo.value = true;
        }

        const onSubmit = _form.handleSubmit(_submitForm, _onInvalidSubmit);

        return {
            isMounted,
            fields,

            excavationType,
            excavationMethod,
            isKlipDecree,
            startDate,
            endDate,
            excavationDepth,
            includeImkl,
            reference,
            excavationLocation,
            excavationDescriptionRequired,
            excavationDescription,
            tags,
            isOrganisation,
            organisationId,
            role,
            addressAdditionalLine,
            address,
            telephone,
            emailAddress,
            useAlternativeEmail,
            alternateEmailAddress,

            showPeriodMessage,

            editContactDetails,
            editContactDetailsMode,
            rememberContactDetails,
            showStep2ValidationInfo,
            contactDetails,

            startDateMinDate,
            endDateMinDate,

            showOrgDropDown,
            showContactCard,

            formatCurrency,

            setDepth,
            excavationDescriptionWarning,
            userHasNoEmail,
            canOrderImkl,
            applyVat,
            contactAddress,
            contactRole,

            mapRequestRoles,

            onSubmit,

            fieldWidth: '8',
            fieldWidthHalf: '4',
            fieldWidthS: '12',
            fieldWidthHalfS: '6',
            fieldWidthXS: '12',
        }
    }
});
